import { isSSR } from 'lib/helpers/isSSR/isSSR'

const SEGMENT_QUEUE_KEY_PREFIX = 'persisted-queue:'

// localStorage strings are UTF-16, so 500k chars ≈ 1MB on disk — far above any healthy
// retry backlog (a handful of KB) and well below the ~5MB origin quota the queue can exhaust.
export const OVERSIZED_QUEUE_THRESHOLD_CHARS = 500_000

/**
 * Removes Segment SDK retry-queue entries (`persisted-queue:*` localStorage keys) that have
 * grown abnormally large. When a destination never becomes flushable (e.g. an ad blocker keeps
 * GTM from loading), the SDK re-serializes its entire undelivered queue on every event until
 * `setItem` exhausts the origin quota — breaking localStorage for the whole app.
 *
 * Must run before `AnalyticsBrowser.load` so the SDK boots from the cleaned state. Only drops
 * stale undelivered analytics events; healthy queues, Segment identity, and app keys are kept.
 * Safe to call anywhere: no-ops on the server and when localStorage access is blocked.
 */
export const pruneOversizedSegmentQueues = () => {
  if (isSSR()) {
    return
  }

  try {
    const oversizedQueueKeys = Object.keys(window.localStorage).filter(
      (key) =>
        key.startsWith(SEGMENT_QUEUE_KEY_PREFIX) &&
        (window.localStorage.getItem(key)?.length ?? 0) > OVERSIZED_QUEUE_THRESHOLD_CHARS,
    )

    oversizedQueueKeys.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // localStorage can be unavailable entirely (privacy mode, blocked cookies) — cleanup is best-effort
  }
}
